/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Search, Inbox, AlertTriangle, ArrowRight, User } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const STAGES = [
    { value: "waiting_on_client", label: "Waiting on client", tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { value: "processing", label: "Processing", tone: "bg-sky-50 text-sky-800 border-sky-200" },
    { value: "review", label: "Review", tone: "bg-violet-50 text-violet-800 border-violet-200" },
    { value: "ready_to_file", label: "Ready to file", tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
    { value: "filed", label: "Filed", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
];
const PRIORITIES = [
    { value: "urgent", label: "Urgent", tone: "bg-rose-100 text-rose-800 border-rose-200" },
    { value: "high", label: "High", tone: "bg-amber-100 text-amber-800 border-amber-200" },
    { value: "normal", label: "Normal", tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
    { value: "low", label: "Low", tone: "bg-zinc-50 text-zinc-500 border-zinc-200" },
];

const labelOf = (arr, v) => arr.find((x) => x.value === v)?.label || v;
const toneOf = (arr, v) => arr.find((x) => x.value === v)?.tone || "bg-zinc-100 text-zinc-700 border-zinc-200";

function fmtRelative(iso) {
    if (!iso) return "—";
    const ms = Date.now() - new Date(iso).getTime();
    const d = Math.floor(ms / 86400000);
    if (d <= 0) return "today";
    if (d === 1) return "1 day ago";
    if (d < 30) return `${d} days ago`;
    return `${Math.floor(d / 30)}mo ago`;
}
function slaState(item) {
    if (!item.sla_due_at) return { tone: "text-[#5E636E]", label: "—" };
    const ms = new Date(item.sla_due_at).getTime() - Date.now();
    const days = Math.round(ms / 86400000);
    if (ms < 0) return { tone: "text-rose-700 font-medium", label: `${Math.abs(days)}d overdue` };
    if (days <= 1) return { tone: "text-amber-700 font-medium", label: days <= 0 ? "due today" : "1d left" };
    return { tone: "text-[#5E636E]", label: `${days}d left` };
}

export default function WorkQueue() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [filterStage, setFilterStage] = useState("all");
    const [filterAssigned, setFilterAssigned] = useState("all");
    const [filterPriority, setFilterPriority] = useState("all");
    const [search, setSearch] = useState("");
    const [showBreached, setShowBreached] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { open_only: true };
            if (filterStage !== "all") params.stage = filterStage;
            if (filterAssigned !== "all") params.assigned = filterAssigned;
            if (filterPriority !== "all") params.priority = filterPriority;
            if (showBreached) params.breached = true;
            const [w, c, st, s] = await Promise.all([
                api.get("/work-queue/", { params }),
                api.get("/clients/"),
                api.get("/work-queue/stats"),
                api.get("/auth/users").catch(() => ({ data: [] })),
            ]);
            setItems(w.data);
            setClients(c.data);
            setStats(st.data);
            setStaff(s.data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [filterStage, filterAssigned, filterPriority, showBreached]);
    useEffect(() => { load(); }, [load]);

    const clientName = useCallback((id) => clients.find((c) => c.id === id)?.name || "—", [clients]);
    const staffName = useCallback((id) => staff.find((u) => u.id === id)?.name || "Unassigned", [staff]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) =>
            (i.title || "").toLowerCase().includes(q) ||
            (clientName(i.client_id) || "").toLowerCase().includes(q)
        );
    }, [items, search, clientName]);

    async function advanceStage(item) {
        const idx = STAGES.findIndex((s) => s.value === item.stage);
        const next = STAGES[Math.min(idx + 1, STAGES.length - 1)].value;
        if (next === item.stage) return;
        try {
            await api.patch(`/work-queue/${item.id}`, { stage: next });
            toast.success(`Moved to ${labelOf(STAGES, next)}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }
    async function setStage(item, stage) {
        try {
            await api.patch(`/work-queue/${item.id}`, { stage });
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }
    async function setAssigned(item, userId) {
        try {
            await api.patch(`/work-queue/${item.id}`, { assigned_staff_user_id: userId === "__unassigned__" ? null : userId });
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="work-queue-page">
            <PageHeader
                eyebrow="Production line"
                title="Work queue"
                description="Every request, document, return and reply that needs attention — sorted by priority and SLA. The kitchen display for the firm."
                testId="work-queue-header"
            />

            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5" data-testid="work-queue-stats">
                    <StatTile label="Blocked today" value={stats.clients_blocked_today} tone="text-rose-700" testId="stat-blocked" />
                    <StatTile label="Ready to process" value={stats.ready_for_processing} testId="stat-processing" />
                    <StatTile label="Waiting on client" value={stats.waiting_on_client} tone="text-amber-700" testId="stat-waiting" />
                    <StatTile label="Ready for review" value={stats.ready_for_review} testId="stat-review" />
                    <StatTile label="Ready to file" value={stats.ready_to_file} testId="stat-rtf" />
                    <StatTile label="Avg days waiting" value={stats.avg_days_waiting} suffix="d" testId="stat-avg" />
                    <StatTile label="Oldest item" value={stats.queue_age_days} suffix="d" tone={stats.queue_age_days > 14 ? "text-rose-700" : ""} testId="stat-age" />
                </div>
            )}

            <div className="mb-3 flex flex-wrap gap-2 items-center">
                <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#5E636E]" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title or client…" className="pl-9 h-9 w-64" data-testid="wq-search" />
                </div>
                <Select value={filterStage} onValueChange={setFilterStage}>
                    <SelectTrigger className="w-44 h-9" data-testid="wq-filter-stage"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All stages</SelectItem>
                        {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filterAssigned} onValueChange={setFilterAssigned}>
                    <SelectTrigger className="w-44 h-9" data-testid="wq-filter-assigned"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All staff</SelectItem>
                        <SelectItem value="me">Assigned to me</SelectItem>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {staff.filter((u) => ["firm_admin", "firm_staff"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                    <SelectTrigger className="w-36 h-9" data-testid="wq-filter-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All priorities</SelectItem>
                        {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button
                    variant={showBreached ? "default" : "outline"}
                    onClick={() => setShowBreached((v) => !v)}
                    className={`h-9 text-xs ${showBreached ? "bg-rose-600 hover:bg-rose-700" : ""}`}
                    data-testid="wq-filter-breached"
                >
                    <AlertTriangle className="h-3.5 w-3.5" /> SLA breached
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16 text-[#5E636E]"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : filtered.length === 0 ? (
                <EmptyState icon={Inbox} title="No items match" description="Try widening your filters." testId="wq-empty" />
            ) : (
                <div className="overflow-hidden rounded-lg border border-[#E4E4E7] bg-white" data-testid="wq-table">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F4EF] text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">
                            <tr>
                                <th className="px-3 py-2 text-left">Pri</th>
                                <th className="px-3 py-2 text-left">Title</th>
                                <th className="px-3 py-2 text-left">Client</th>
                                <th className="px-3 py-2 text-left">Stage</th>
                                <th className="px-3 py-2 text-left">Assigned</th>
                                <th className="px-3 py-2 text-left">SLA</th>
                                <th className="px-3 py-2 text-left">Aged</th>
                                <th className="px-3 py-2 text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((i) => {
                                const sla = slaState(i);
                                return (
                                    <tr key={i.id} className="border-t border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`wq-row-${i.id}`}>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${toneOf(PRIORITIES, i.priority)}`}>{labelOf(PRIORITIES, i.priority)}</span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-[13px] truncate max-w-[28ch]">{i.title}</div>
                                            {i.blocking_reason && <div className="text-[11px] text-amber-700 truncate max-w-[28ch]">⏸ {i.blocking_reason}</div>}
                                        </td>
                                        <td className="px-3 py-2 truncate max-w-[18ch]">{clientName(i.client_id)}</td>
                                        <td className="px-3 py-2">
                                            <Select value={i.stage} onValueChange={(v) => setStage(i, v)}>
                                                <SelectTrigger className={`h-7 w-[140px] text-[11px] border ${toneOf(STAGES, i.stage)}`} data-testid={`wq-stage-${i.id}`}><SelectValue /></SelectTrigger>
                                                <SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                            </Select>
                                        </td>
                                        <td className="px-3 py-2">
                                            <Select value={i.assigned_staff_user_id || "__unassigned__"} onValueChange={(v) => setAssigned(i, v)}>
                                                <SelectTrigger className="h-7 w-[140px] text-[11px]" data-testid={`wq-assign-${i.id}`}>
                                                    <span className="truncate inline-flex items-center gap-1.5"><User className="h-3 w-3" />{i.assigned_staff_user_id ? staffName(i.assigned_staff_user_id) : "Unassigned"}</span>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="__unassigned__">Unassigned</SelectItem>
                                                    {staff.filter((u) => ["firm_admin", "firm_staff"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className={`px-3 py-2 text-xs ${sla.tone}`}>{sla.label}</td>
                                        <td className="px-3 py-2 text-xs text-[#5E636E]">{fmtRelative(i.created_at)}</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => advanceStage(i)} disabled={i.stage === "filed"} data-testid={`wq-advance-${i.id}`}>
                                                Next <ArrowRight className="h-3.5 w-3.5" />
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function StatTile({ label, value, tone = "", suffix = "", testId }) {
    return (
        <div className="rounded-lg border border-[#E4E4E7] bg-white p-3" data-testid={testId}>
            <div className="co-label">{label}</div>
            <div className={`mt-1 font-display text-xl tabular-nums ${tone}`}>{value ?? "—"}{suffix}</div>
        </div>
    );
}
